import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { ChevronLeft, Loader2, Trophy, Flame, MapPin, Scale, ChevronUp, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { countryCodeToFlag } from '../utils/countryFlag';

const API = process.env.REACT_APP_BACKEND_URL;

const METRICS = [
  { id: 'distance',    icon: MapPin,  i18n: 'leaderboard.metricDistance', fallback: 'Distanza' },
  { id: 'calories',    icon: Flame,   i18n: 'leaderboard.metricCalories', fallback: 'Calorie' },
  { id: 'weight_lost', icon: Scale,   i18n: 'leaderboard.metricWeightLost', fallback: 'Peso perso' },
];
const PERIODS = [
  { id: 'week',  i18n: 'leaderboard.periodWeek',  fallback: 'Settimana' },
  { id: 'month', i18n: 'leaderboard.periodMonth', fallback: 'Mese' },
];

const RANK_STYLES = {
  1: { medal: '🥇', bg: 'bg-gradient-to-r from-yellow-400/20 to-amber-500/20', ring: 'ring-yellow-400' },
  2: { medal: '🥈', bg: 'bg-gradient-to-r from-gray-300/20 to-gray-400/20',     ring: 'ring-gray-300' },
  3: { medal: '🥉', bg: 'bg-gradient-to-r from-amber-700/20 to-orange-700/20',  ring: 'ring-amber-700' },
};

export default function Classifica() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { user } = useAuth();

  const [metric, setMetric] = useState('distance');
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/leaderboards/country`, {
        params: { metric, period },
        withCredentials: true,
      });
      setData(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('leaderboard.errorLoading', 'Errore caricamento'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [metric, period, t]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  const country = data?.country || (user?.country || user?.detected_country || '').toUpperCase();
  const flag = countryCodeToFlag(country);
  const unit = data?.unit || 'km';
  const formatScore = (s) => {
    if (s == null) return '—';
    if (unit === 'km') return `${Number(s).toFixed(1)} km`;
    if (unit === 'kcal') return `${Math.round(Number(s))} kcal`;
    if (unit === 'kg') return `${Number(s).toFixed(1)} kg`;
    return String(s);
  };

  return (
    <div className={`${isDark ? 'bg-[#0D1424]' : 'bg-white'}`} data-testid="classifica-page">
      {/* Fixed Header — matches MieRicette pattern (with safe area) */}
      <div
        className={`fixed-header ${isDark ? 'bg-[#0D1424]' : 'bg-white'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}
      >
        <div className="max-w-md mx-auto">
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className={`p-1.5 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
            aria-label={t('common.back', 'Indietro')}
            data-testid="leaderboard-back-btn"
          >
            <ChevronLeft className={`w-6 h-6 ${isDark ? 'text-white' : 'text-gray-900'}`} />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              <h1 className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('leaderboard.title', 'Classifica')}{flag && ` ${flag}`}
              </h1>
            </div>
            {data?.total_users > 0 && (
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('leaderboard.totalUsers', { count: data.total_users, defaultValue: `${data.total_users} utenti in ${country}` })}
              </p>
            )}
          </div>
        </div>

        {/* Metric tabs */}
        <div className="flex gap-1.5 px-4 pb-2 overflow-x-auto no-scrollbar">
          {METRICS.map((m) => {
            const Icon = m.icon;
            const active = metric === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setMetric(m.id)}
                data-testid={`leaderboard-metric-${m.id}`}
                className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  active
                    ? 'bg-[#E8334A] text-white'
                    : isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t(m.i18n, m.fallback)}
              </button>
            );
          })}
        </div>

        {/* Period tabs */}
        <div className="flex gap-1.5 px-4 pb-3">
          {PERIODS.map((p) => {
            const active = period === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                data-testid={`leaderboard-period-${p.id}`}
                className={`flex-1 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  active
                    ? (isDark ? 'bg-white text-[#0D1424]' : 'bg-[#0D1424] text-white')
                    : isDark ? 'bg-white/5 text-white/50' : 'bg-gray-100/50 text-gray-500'
                }`}
              >
                {t(p.i18n, p.fallback)}
              </button>
            );
          })}
        </div>
        </div>
      </div>

      {/* Content — same pattern as MieRicette: content-below-header-xl + pb-24 + spacer */}
      <div className="content-below-header-xl pb-24 px-4" data-testid="leaderboard-body">
        <div className="h-10"></div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#E8334A] animate-spin" />
          </div>
        )}

        {!loading && error && (
          <div className={`rounded-2xl p-6 text-center ${isDark ? 'bg-white/5 text-white/70' : 'bg-white text-gray-600'}`}>
            <p>{error}</p>
            {/Paese non impostato/.test(error) && (
              <button
                onClick={() => navigate('/profilo')}
                className="mt-3 text-[#E8334A] font-semibold"
              >
                {t('leaderboard.setCountryCta', 'Imposta il tuo paese')}
              </button>
            )}
          </div>
        )}

        {!loading && !error && data && data.top.length === 0 && (
          <div className={`rounded-2xl p-8 text-center ${isDark ? 'bg-white/5 text-white/70' : 'bg-white text-gray-500'}`}>
            <Trophy className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
            <p className="font-medium">
              {t('leaderboard.empty', { country })}
            </p>
            <p className="text-sm mt-2">
              {t('leaderboard.emptyHint', "Sii il primo a registrare un'attività e prendi la vetta!")}
            </p>
          </div>
        )}

        {!loading && !error && data && data.top.length > 0 && (
          <>
            {/* Top 3 podium-style — visually richer */}
            <div className="space-y-2 mb-4">
              {data.top.slice(0, 3).map((entry) => {
                const style = RANK_STYLES[entry.rank];
                return (
                  <div
                    key={entry.user_id}
                    onClick={() => navigate(`/app/utente/${entry.user_id}`)}
                    role="button"
                    data-testid={`leaderboard-top3-rank-${entry.rank}`}
                    className={`flex items-center gap-3 p-3 rounded-2xl cursor-pointer ${style.bg} ${isDark ? 'hover:bg-white/10' : 'hover:bg-white'}`}
                  >
                    <div className="text-2xl w-8 text-center">{style.medal}</div>
                    {entry.profile_photo ? (
                      <img
                        src={entry.profile_photo.startsWith('http') ? entry.profile_photo : `${API}${entry.profile_photo}`}
                        alt=""
                        className={`w-12 h-12 rounded-full object-cover ring-2 ${style.ring}`}
                      />
                    ) : (
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ring-2 ${style.ring} ${isDark ? 'bg-white/10 text-white/70' : 'bg-gray-200 text-gray-600'}`}>
                        {(entry.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{entry.name}</div>
                      {entry.username && (
                        <div className={`text-xs truncate ${isDark ? 'text-white/40' : 'text-gray-400'}`}>@{entry.username}</div>
                      )}
                    </div>
                    <div className={`font-mono font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {formatScore(entry.score)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Rank 4-10 list */}
            {data.top.length > 3 && (
              <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                {data.top.slice(3).map((entry) => (
                  <div
                    key={entry.user_id}
                    onClick={() => navigate(`/app/utente/${entry.user_id}`)}
                    role="button"
                    data-testid={`leaderboard-rank-${entry.rank}`}
                    className={`flex items-center gap-3 p-3 cursor-pointer border-t ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'} first:border-t-0`}
                  >
                    <div className={`w-8 text-center font-mono text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      {entry.rank}
                    </div>
                    {entry.profile_photo ? (
                      <img
                        src={entry.profile_photo.startsWith('http') ? entry.profile_photo : `${API}${entry.profile_photo}`}
                        alt=""
                        className="w-9 h-9 rounded-full object-cover"
                      />
                    ) : (
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold ${isDark ? 'bg-white/10 text-white/70' : 'bg-gray-200 text-gray-600'}`}>
                        {(entry.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className={`flex-1 truncate font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {entry.name}
                    </div>
                    <div className={`font-mono text-sm ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                      {formatScore(entry.score)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* My position */}
            <div
              className={`mt-6 p-4 rounded-2xl border-2 ${isDark ? 'bg-white/5 border-[#E8334A]/40' : 'bg-white border-[#E8334A]/30'}`}
              data-testid="leaderboard-my-position"
            >
              <div className={`text-xs uppercase tracking-wide mb-2 font-semibold ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                {t('leaderboard.myPosition', 'La tua posizione')}
              </div>
              {data.my_rank ? (
                <>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>#{data.my_rank}</span>
                    <span className={`${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                      {t('leaderboard.outOf', { count: data.ranked_count, defaultValue: `su ${data.ranked_count}` })}
                    </span>
                  </div>
                  <div className={`mt-1 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                    {formatScore(data.my_score)}
                    {data.users_below_me > 0 && (
                      <span className={`ml-3 inline-flex items-center gap-1 text-sm font-semibold text-green-500`}>
                        <ChevronUp className="w-4 h-4" />
                        {t('leaderboard.aheadOf', { count: data.users_below_me, defaultValue: `Davanti a ${data.users_below_me}` })}
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <div className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  <ChevronDown className="inline w-4 h-4" />{' '}
                  {t('leaderboard.notRankedYet', 'Registra un\'attività per entrare in classifica')}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
